import { Paper } from '@material-ui/core'
import { LOCALES_SUPPORTED_CUSTOMER_FACING } from 'constants/locales'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import ChipsInput from 'elements/input/ChipsInput'
import { PRODUCT_FILTERS } from 'expo/utils'
import { Link, styled } from 'opensolar-ui'
import { useMemo } from 'react'
import { BooleanInput, ReferenceInput, SelectInput, SimpleForm, TextInput, useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { getLocales } from 'resources/documentTemplates/util'
import { useResourceStyles } from 'resources/styles'
import { required } from 'validations'
import InputAccordion from './common/InputAccordion'
import ContentFields from './ContentFields'
import { formatSubmitValues, validateExpoForm } from './form'
import LocaleAutoFill from './LocaleAutoFill'
import PageContentField from './PageContentField'
import PreviewTemplateButton from './PreviewTemplateButton'
import { defaultExpoContents } from './util'


const InputWrapper = styled(Paper, { name: 'InputWrapper' })({
  display: 'flex',
  flexDirection: 'column',
})
const DEFAULT_TAG_OPTIONS = PRODUCT_FILTERS.map((tag) => ({ id: tag, name: tag }))

const normalizeFormData = (record, isPremiumExpo) => {
  if (!record) return record

  const contentBlocks = defaultExpoContents(isPremiumExpo)
  let elements = record.contents?.elements || []

  elements = elements.map((element) => {
    if (element.contentType === 'cover') {
      return { ...element, enabled: isPremiumExpo }
    }
    return element
  })

  const existingContentTypes = new Set(elements.map((element) => element.contentType))
  const requiredContentTypes = new Set(contentBlocks.map((block) => block.contentType))

  const newBlocks = contentBlocks.filter((block) => !existingContentTypes.has(block.contentType))
  const removeBlocks = new Set(
    elements.filter((element) => !requiredContentTypes.has(element.contentType)).map((element) => element.contentType)
  )

  if (newBlocks.length) {
    elements = elements.concat(newBlocks)
  }
  if (removeBlocks.size) {
    elements = elements.filter((element) => !removeBlocks.has(element.contentType))
  }

  return {
    ...record,
    contents: {
      ...(record.contents || {}),
      elements,
    },
  }
}

const ExpoStandForm = (props) => {
  const translate = useTranslate()
  const resourceStyles = useResourceStyles()
  const isAdmin = useSelector(authSelectors.getIsAdmin)
  const isStaff = useSelector(authSelectors.getIsStaff)
  const isPremiumExpo = useSelector(orgSelectors.getPremiumExpo)
  const locales = getLocales(LOCALES_SUPPORTED_CUSTOMER_FACING)
  const disabled = useMemo(() => !isAdmin || props.disabled, [props.disabled, isAdmin])
  const normalizedRecord = useMemo(() => normalizeFormData(props.record, isPremiumExpo), [props.record, isPremiumExpo])

  return (
    <SimpleForm
      {...props}
      record={normalizedRecord}
      formatSubmitValues={formatSubmitValues}
      validate={validateExpoForm}
    >
      <PreviewTemplateButton />
      <InputWrapper elevation={1} className={resourceStyles.formSection}>
        <TextInput label={'Title'} disabled={disabled} name="title" source="title" validate={required} />
        <TextInput label={'Description'} disabled={disabled} name="description" source="description" multiline={true} />
        <ChipsInput source={'tags'} choices={DEFAULT_TAG_OPTIONS} allChoices={DEFAULT_TAG_OPTIONS} label={'Tags'} />
      </InputWrapper>
      <InputAccordion title={translate('Restricted Access (OpenSolar Staff Only)')} source={'countries_include'}>
        <TextInput
          label={'Countries Included'}
          disabled={disabled}
          fullWidth={true}
          name="countries_include"
          source="countries_include"
          validate={required}
        />
        <LocaleAutoFill disabled={disabled} locales={locales} />
        <BooleanInput label={'Published'} disabled={disabled || !isStaff} source="is_published" name="is_published" />
        <ReferenceInput
          name="brand"
          resource="brands"
          source="brand"
          reference="brands"
          allowEmpty={true}
          perPage={1000}
          fullWidth={true}
          style={{ width: '100%' }}
          renderChildrenBeforeRecord={true}
          label="Brand"
        >
          <SelectInput
            source={'url'}
            optionText="name"
            optionValue="url"
            disabled={disabled || !isStaff}
            helperText={isStaff ? <Link href="/brands">Configure Brands</Link> : undefined}
          />
        </ReferenceInput>
      </InputAccordion>
      <InputAccordion title={translate('Expo Stand Settings')} defaultExpanded={true}>
        <PageContentField />
      </InputAccordion>
      <ContentFields {...props} disabled={disabled} isStaff={isStaff} />
    </SimpleForm>
  )
}

export default ExpoStandForm
